// packages/shared/api-client/endpoints/auth.js
// Authentication API endpoints

import { baseApi } from "../baseApi";

/**
 * Auth API endpoints
 * Provides login, register, logout, password reset, etc.
 */
const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Login mutation
    login: builder.mutation({
      query: (credentials) => ({
        url: "/api/auth/login",
        method: "POST",
        body: credentials,
      }),
      // Invalidate user cache on successful login
      invalidatesTags: ["User"],
    }),

    // Register mutation
    register: builder.mutation({
      query: (userData) => ({
        url: "/api/auth/register",
        method: "POST",
        body: userData,
      }),
    }),

    // Refresh token mutation
    refreshToken: builder.mutation({
      query: (refreshToken) => ({
        url: "/api/auth/refresh",
        method: "POST",
        body: { refreshToken },
      }),
    }),

    // Logout mutation
    logout: builder.mutation({
      query: (tokens) => ({
        url: "/api/auth/logout",
        method: "POST",
        body: tokens,
      }),
      // Invalidate all caches on logout
      invalidatesTags: ["User", "Post", "Grievance", "Event", "Document"],
    }),

    // Forgot password mutation
    forgotPassword: builder.mutation({
      query: (email) => ({
        url: "/api/auth/forgot-password",
        method: "POST",
        body: { email },
      }),
    }),

    // Reset password mutation
    resetPassword: builder.mutation({
      query: ({ token, newPassword }) => ({
        url: "/api/auth/reset-password",
        method: "POST",
        body: { token, newPassword },
      }),
    }),

    // Change password mutation
    changePassword: builder.mutation({
      query: ({ currentPassword, newPassword }) => ({
        url: "/api/auth/change-password",
        method: "POST",
        body: { currentPassword, newPassword },
      }),
    }),

    // Validate invite token
    validateInvite: builder.query({
      query: (token) => ({
        url: `/api/invitations/verify/${token}`,
        method: "GET",
      }),
    }),

    // Check account status
    checkAccountStatus: builder.query({
      query: () => ({
        url: "/api/auth/status",
        method: "GET",
      }),
    }),
  }),
});

// Export hooks for usage in components
export const useLoginMutation = authApi.useLoginMutation;
export const useRegisterMutation = authApi.useRegisterMutation;
export const useRefreshTokenMutation = authApi.useRefreshTokenMutation;
export const useLogoutMutation = authApi.useLogoutMutation;
export const useForgotPasswordMutation = authApi.useForgotPasswordMutation;
export const useResetPasswordMutation = authApi.useResetPasswordMutation;
export const useChangePasswordMutation = authApi.useChangePasswordMutation;
export const useValidateInviteQuery = authApi.useValidateInviteQuery;
export const useCheckAccountStatusQuery = authApi.useCheckAccountStatusQuery;
